import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, Select, Switch, Typography, message, Popconfirm, Tooltip, Tabs, Checkbox, Row, Col, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const PERFIL_COR = { admin: 'red', gerente: 'orange', tecnico: 'blue', engenheiro: 'cyan', comercial: 'green', financeiro: 'purple', usuario: 'default' };
const PERFIL_LABEL = { admin: 'Administrador', gerente: 'Gerente', tecnico: 'Técnico', engenheiro: 'Engenheiro', comercial: 'Comercial', financeiro: 'Financeiro', usuario: 'Usuário' };

function PerfisTab({ perfis, setPerfis }) {
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form] = Form.useForm();

  const salvar = async (values) => {
    try {
      if (editando) {
        const r = await api.put('/perfis/' + editando.id, values);
        setPerfis(prev => prev.map(p => p.id === editando.id ? r.data : p));
      } else {
        const r = await api.post('/perfis', values);
        setPerfis(prev => [...prev, r.data]);
      }
      message.success('Perfil salvo!');
      setModal(false);
      form.resetFields();
      setEditando(null);
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  const excluir = async (id) => {
    try {
      await api.delete('/perfis/' + id);
      setPerfis(prev => prev.filter(p => p.id !== id));
      message.success('Perfil removido!');
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  return (
    <>
    <Card size="small" extra={<Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => { setEditando(null); form.resetFields(); setModal(true); }}>Novo Perfil</Button>}>
      <Table dataSource={perfis} rowKey="id" size="small" pagination={false}
        columns={[
          { title: 'Nome', dataIndex: 'nome', width: 150, render: v => <Tag color={PERFIL_COR[v] || 'default'}>{v}</Tag> },
          { title: 'Ações', width: 80, render: (_, r) => (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} onClick={() => { setEditando(r); form.setFieldsValue(r); setModal(true); }} />
              {r.nome !== 'admin' && <Popconfirm title="Remover perfil?" onConfirm={() => excluir(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
            </Space>
          )},
          { title: 'Descrição', dataIndex: 'descricao', ellipsis: true },
        ]}
      />
    </Card>
    <Modal title={editando ? 'Editar Perfil' : 'Novo Perfil'} open={modal} onCancel={() => { setModal(false); setEditando(null); form.resetFields(); }} footer={null}>
      <Form form={form} layout="vertical" onFinish={salvar}>
        <Form.Item name="nome" label="Nome do Perfil" rules={[{ required: true }]} extra="Use letras minúsculas sem espaços (ex: tecnico, gerente)"><Input /></Form.Item>
        <Form.Item name="descricao" label="Descrição"><Input placeholder="Descrição do perfil..." /></Form.Item>
        <Button type="primary" htmlType="submit" block>Salvar</Button>
      </Form>
    </Modal>
    </>
  );
}

const MODULOS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'propostas', label: 'Propostas' },
  { key: 'produtos', label: 'Produtos' },
  { key: 'estoque', label: 'Estoque' },
  { key: 'leads', label: 'Leads' },
  { key: 'gestao', label: 'Gestão' },
  { key: 'laudos', label: 'Laudos' },
  { key: 'fiscal', label: 'Nota Fiscal' },
  { key: 'boletos', label: 'Boletos' },
  { key: 'os', label: 'OS/Agendamentos' },
  { key: 'envio_massa', label: 'Envio em Massa' },
  { key: 'usuarios', label: 'Usuários' },
  { key: 'templates', label: 'Templates' },
  { key: 'configuracoes', label: 'Configurações' },
  { key: 'classificacao_nr13', label: 'Classificação NR-13' },
  { key: 'relatorios', label: 'Relatórios' },
];



function PermissoesTab({ perfis = [] }) {
  const [perfilSel, setPerfilSel] = useState('tecnico');
  const [permissoes, setPermissoes] = useState({});
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregarPermissoes(perfilSel); }, [perfilSel]);

  const carregarPermissoes = async (perfil) => {
    setLoading(true);
    try {
      const r = await api.get('/permissoes/' + perfil);
      setPermissoes(r.data);
    } catch(e) { message.error('Erro ao carregar permissões'); }
    finally { setLoading(false); }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.post('/permissoes/' + perfilSel, { permissoes });
      message.success('Permissões salvas!');
    } catch(e) { message.error('Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const toggle = (modulo, tipo) => {
    setPermissoes(prev => ({
      ...prev,
      [modulo]: { ...prev[modulo], [tipo]: !prev[modulo]?.[tipo] }
    }));
  };

  const toggleTodos = (tipo, valor) => {
    setPermissoes(prev => {
      const novo = { ...prev };
      for (const mod of MODULOS) {
        novo[mod.key] = { ...novo[mod.key], [tipo]: valor };
      }
      return novo;
    });
  };

  return (
    <Card size="small">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <label style={{ fontSize: 12, color: '#888' }}>Perfil</label>
          <Select style={{ width: '100%' }} value={perfilSel} onChange={setPerfilSel}>
            {perfis.map(p => <Option key={p.nome} value={p.nome}>{p.nome.charAt(0).toUpperCase() + p.nome.slice(1)}</Option>)}
          </Select>
        </Col>
        <Col span={16} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={salvar} loading={salvando}>Salvar Permissões</Button>
        </Col>
      </Row>
      <Table
        dataSource={MODULOS}
        rowKey="key"
        size="small"
        loading={loading}
        pagination={false}
        columns={[
          { title: 'Módulo', dataIndex: 'label', width: 200 },
          { title: <span>Ver <Checkbox onChange={e => toggleTodos('ver', e.target.checked)} /></span>, width: 80, render: (_, r) => <Checkbox checked={permissoes[r.key]?.ver} onChange={() => toggle(r.key, 'ver')} /> },
          { title: <span>Inserir <Checkbox onChange={e => toggleTodos('inserir', e.target.checked)} /></span>, width: 80, render: (_, r) => <Checkbox checked={permissoes[r.key]?.inserir} onChange={() => toggle(r.key, 'inserir')} /> },
          { title: <span>Editar <Checkbox onChange={e => toggleTodos('editar', e.target.checked)} /></span>, width: 80, render: (_, r) => <Checkbox checked={permissoes[r.key]?.editar} onChange={() => toggle(r.key, 'editar')} /> },
          { title: <span>Excluir <Checkbox onChange={e => toggleTodos('excluir', e.target.checked)} /></span>, width: 80, render: (_, r) => <Checkbox checked={permissoes[r.key]?.excluir} onChange={() => toggle(r.key, 'excluir')} /> },
        ]}
      />
    </Card>
  );
}

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form] = Form.useForm();
  const [perfis, setPerfis] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [filtroUsuarioLog, setFiltroUsuarioLog] = useState('');
  const [filtroTabela, setFiltroTabela] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => { carregarUsuarios(); api.get('/perfis').then(r => setPerfis(r.data || [])).catch(() => {}); }, []);

  const carregarLogs = async (page = 1) => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filtroUsuarioLog) params.append('usuario_id', filtroUsuarioLog);
      if (filtroTabela) params.append('tabela', filtroTabela);
      if (filtroDataInicio) params.append('data_inicio', filtroDataInicio);
      if (filtroDataFim) params.append('data_fim', filtroDataFim);
      const r = await api.get('/logs?' + params.toString());
      setLogs(r.data.logs || []);
      setLogsTotal(r.data.total || 0);
      setLogsPage(page);
    } catch(e) { message.error('Erro ao carregar logs'); }
    finally { setLoadingLogs(false); }
  };

  const carregarUsuarios = async () => {
    const r = await api.get('/usuarios');
    setUsuarios(r.data || []);
  };

  const abrirModal = (usuario = null) => {
    setEditando(usuario);
    form.resetFields();
    if (usuario) form.setFieldsValue({ ...usuario, senha: '' });
    setModalVisible(true);
  };

  const salvar = async (values) => {
    try {
      if (!values.senha) delete values.senha;
      if (editando) {
        await api.put('/usuarios/' + editando.id, values);
        message.success('Usuário atualizado!');
      } else {
        await api.post('/usuarios', values);
        message.success('Usuário criado!');
      }
      setModalVisible(false);
      carregarUsuarios();
    } catch(e) { message.error(e.response?.data?.erro || 'Erro ao salvar'); }
  };

  const desativar = async (id) => {
    await api.delete('/usuarios/' + id);
    carregarUsuarios();
    message.success('Usuário desativado!');
  };

  const colunas = [
    { title: 'Nome', dataIndex: 'nome', render: (v, r) => (
      <Space><UserOutlined style={{ color: r.ativo ? '#1890ff' : '#ccc' }} /><Text style={{ color: r.ativo ? undefined : '#ccc' }}>{v}</Text></Space>
    )},
    { title: 'Email', dataIndex: 'email' },
    { title: 'Perfil', dataIndex: 'perfil', width: 130, render: (v) => <Tag color={PERFIL_COR[v]}>{PERFIL_LABEL[v] || v}</Tag> },
    { title: 'CREA', dataIndex: 'crea', width: 120, render: (v) => v || '-' },
    { title: 'Status', dataIndex: 'ativo', width: 90, render: (v) => v ? <Tag color="green">Ativo</Tag> : <Tag color="red">Inativo</Tag> },
    { title: 'Último acesso', dataIndex: 'ultimo_acesso', width: 150, render: (v) => v ? new Date(v).toLocaleString('pt-BR') : '-' },
    { title: 'Ações', width: 100, render: (_, r) => (
      <Space size={4}>
        <Tooltip title="Editar"><Button icon={<EditOutlined />} size="small" type="primary" onClick={() => abrirModal(r)} /></Tooltip>
        <Popconfirm title="Desativar usuário?" onConfirm={() => desativar(r.id)}>
          <Tooltip title="Desativar"><Button icon={<DeleteOutlined />} size="small" danger /></Tooltip>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Usuários</Title>
      </div>

      <Tabs onChange={(key) => { if (key === 'logs') carregarLogs(1); }} items={[
        {
          key: 'usuarios',
          label: 'Usuários',
          children: (
            <Card size="small" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => abrirModal()}>Novo Usuário</Button>}>
              <Table dataSource={usuarios} columns={colunas} rowKey="id" size="small" />
            </Card>
          )
        },
        {
          key: 'perfis',
          label: 'Perfis',
          children: <PerfisTab perfis={perfis} setPerfis={setPerfis} />
        },
        {
          key: 'permissoes',
          label: 'Permissões por Perfil',
          children: <PermissoesTab perfis={perfis} />
        },
        {
          key: 'logs',
          label: 'Logs de Atividade',
          children: (
            <Card size="small">
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={4}>
                  <Select placeholder="Usuário" allowClear style={{ width: '100%' }} onChange={v => setFiltroUsuarioLog(v || '')}>
                    {usuarios.map(u => <Option key={u.id} value={u.id}>{u.nome}</Option>)}
                  </Select>
                </Col>
                <Col span={4}>
                  <Select placeholder="Módulo" allowClear style={{ width: '100%' }} onChange={v => setFiltroTabela(v || '')}>
                    {['clientes','propostas','laudos','boletos','nfe','os','usuarios','produtos','leads'].map(t => <Option key={t} value={t}>{t}</Option>)}
                  </Select>
                </Col>
                <Col span={4}><Input type="date" value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)} placeholder="Data início" /></Col>
                <Col span={4}><Input type="date" value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)} placeholder="Data fim" /></Col>
                <Col span={4}><Button type="primary" onClick={() => carregarLogs(1)}>Filtrar</Button></Col>
              </Row>
              <Table
                dataSource={logs}
                rowKey="id"
                size="small"
                loading={loadingLogs}
                pagination={{ current: logsPage, total: logsTotal, pageSize: 50, onChange: (p) => carregarLogs(p) }}
                columns={[
                  { title: 'Data/Hora', dataIndex: 'criado_em', width: 140, render: v => dayjs(v).format('DD/MM/YYYY HH:mm:ss') },
                  { title: 'Usuário', dataIndex: 'usuario_nome', width: 120 },
                  { title: 'Ação', dataIndex: 'acao', width: 140, render: v => <Tag color="blue">{v}</Tag> },
                  { title: 'Módulo', dataIndex: 'tabela', width: 100, render: v => <Tag>{v}</Tag> },
                  { title: 'ID', dataIndex: 'registro_id', width: 60 },
                  { title: 'IP', dataIndex: 'ip', width: 120 },
                  { title: 'Detalhes', render: (_, r) => {
                    const d = r.dados_depois || r.dados_antes;
                    if (!d) return '-';
                    const entries = Object.entries(d).filter(([k,v]) => v && k !== 'senha' && k !== 'fotos').slice(0,4);
                    return <Text style={{ fontSize: 11 }}>{entries.map(([k,v]) => k + ': ' + String(v).substring(0,25)).join(' | ')}</Text>;
                  }},
                ]}
              />
            </Card>
          )
        }
      ]} />

      <Modal title={editando ? 'Editar Usuário' : 'Novo Usuário'} open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={500}>
        <Form form={form} layout="vertical" onFinish={salvar}>
          <Form.Item name="nome" label="Nome completo" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
          <Form.Item name="perfil" label="Perfil de acesso" rules={[{ required: true }]} initialValue="tecnico">
            <Select>
              {perfis.map(p => <Option key={p.nome} value={p.nome}><Tag color={PERFIL_COR[p.nome] || 'default'}>{p.nome.charAt(0).toUpperCase() + p.nome.slice(1)}</Tag></Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="crea" label="CREA (para técnicos)"><Input placeholder="Ex: 45266MG" /></Form.Item>
          <Form.Item name="qualificacao_end" label="Qualificação END"
            extra="Certificação de ensaio não destrutivo, ex: ABENDI SNQC LP N2 nº 12345">
            <Input placeholder="ABENDI SNQC LP N2 nº 12345" />
          </Form.Item>
          <Form.Item name="qualificacao_end_validade" label="Validade da qualificação END">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="assinatura_texto" label="Texto abaixo da assinatura"
            extra="O que sai embaixo da linha, no laudo. Ex: cargo ou registro.">
            <Input placeholder="Ex: Inspetor de END" />
          </Form.Item>
          {editando && editando.id && (
            <Form.Item label="Imagem da assinatura"
              extra={editando.assinatura_chave ? 'Já existe uma imagem enviada. Enviar outra substitui.' : 'PNG com fundo transparente fica melhor no laudo.'}>
              <input type="file" accept="image/*" onChange={async (e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('arquivo', f);
                try {
                  message.loading({ content: 'Enviando assinatura...', key: 'ass', duration: 0 });
                  await api.post('/upload/assinatura/' + editando.id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  message.success({ content: 'Assinatura enviada', key: 'ass' });
                } catch (err) {
                  message.error({ content: 'Erro ao enviar: ' + (err.response?.data?.erro || err.message), key: 'ass' });
                }
              }} />
            </Form.Item>
          )}
          <Form.Item name="senha" label={editando ? 'Nova senha (deixe em branco para manter)' : 'Senha'} rules={[{ required: !editando, message: 'Informe a senha' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={editando ? 'Deixe em branco para manter' : 'Mínimo 6 caracteres'} />
          </Form.Item>
          {editando && (
            <Form.Item name="ativo" label="Status" valuePropName="checked" initialValue={true}>
              <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
            </Form.Item>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setModalVisible(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit">Salvar</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
